import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DollarSign, TrendingUp, AlertTriangle, Clock, Users, FileText,
  ChevronRight, BarChart3, RefreshCw, Filter, X, ListChecks,
  ShieldAlert, ArrowDown, ArrowUp, Minus, Info
} from 'lucide-react';

const fmt = (c) => { if (c == null) return '$0'; const neg = c < 0; const s = (Math.abs(c)/100).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}); return neg ? `-$${s}` : `$${s}`; };
const fmtK = (c) => { if (c == null) return '$0'; const d = Math.abs(c)/100; return d >= 1000 ? `$${(d/1000).toFixed(1)}k` : fmt(c); };

// ─── TIER BADGE ───
function TierBadge({ tier }) {
  const colors = { excellent: 'bg-emerald-100 text-emerald-700', stable: 'bg-sky-100 text-sky-700', watch: 'bg-amber-100 text-amber-700', poor: 'bg-red-100 text-red-700' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[tier] || 'bg-muted text-muted-foreground'}`} data-testid={`tier-${tier}`}>{tier}</span>;
}

// ─── TREND ICON ───
function TrendIcon({ dir }) {
  if (dir === 'worsening') return <ArrowDown className="h-3 w-3 text-red-500" />;
  if (dir === 'improving') return <ArrowUp className="h-3 w-3 text-emerald-500" />;
  return <Minus className="h-3 w-3 text-muted-foreground" />;
}

// ─── KPI CARD ───
function Kpi({ title, value, sub, icon: Icon, color = '', testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className={`text-xl font-bold tracking-tight ${color}`}>{value}</p>
            {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          {Icon && <div className="rounded-md bg-muted/50 p-1.5"><Icon className="h-3.5 w-3.5 text-muted-foreground" /></div>}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── CLAIM DETAIL PANEL ───
function ClaimPanel({ id, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { if (!id) return; setLoading(true); api.get(`/rcm-analytics/claim-detail/${id}`).then(r => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false)); }, [id]);
  if (!id) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose} data-testid="claim-detail-panel">
      <div className="w-full max-w-xl bg-card border-l shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-card border-b p-4 flex justify-between z-10">
          <h3 className="font-semibold text-sm">Claim Detail</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-claim-detail"><X className="h-4 w-4" /></Button>
        </div>
        {loading ? <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div> : !d || d.error ? <div className="p-8 text-center text-red-500 text-sm">Not found</div> : (
          <div className="p-4 space-y-4">
            <Card><CardContent className="p-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge>{d.claim.claim_status}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Payer</span><span>{d.claim.payer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span>{d.claim.service_date?.slice(0,10)}</span></div>
              <div className="border-t pt-1.5 mt-1.5" />
              <div className="flex justify-between"><span>Charge</span><span className="font-medium">{fmt(d.claim.total_charge_amount)}</span></div>
              <div className="flex justify-between"><span>Expected</span><span>{fmt(d.claim.expected_allowed_amount)}</span></div>
              <div className="flex justify-between"><span>Paid</span><span className="text-emerald-600 font-medium">{fmt(d.claim.total_paid_amount)}</span></div>
              <div className="flex justify-between"><span>Adjustments</span><span>{fmt(d.claim.total_adjustment_amount)}</span></div>
              <div className="flex justify-between font-medium"><span>Balance</span><span>{fmt(d.claim.current_balance_amount)}</span></div>
            </CardContent></Card>
            {d.timeline?.length > 0 && <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Timeline</CardTitle></CardHeader><CardContent className="px-3 pb-3">
              <div className="space-y-2">{d.timeline.map((ev,i) => (
                <div key={i} className="flex items-start gap-2 text-xs"><div className="w-1.5 h-1.5 rounded-full bg-primary mt-1 shrink-0" /><div><span className="font-medium">{ev.event_type.replace(/_/g,' ')}</span> <span className="text-muted-foreground">{ev.occurred_at?.slice(0,10)}</span>{ev.amount != null && <span className="ml-1">{fmt(ev.amount)}</span>}</div></div>
              ))}</div>
            </CardContent></Card>}
            {d.denials?.length > 0 && <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-red-600">Denials</CardTitle></CardHeader><CardContent className="px-3 pb-3 space-y-1">
              {d.denials.map((dn,i) => <div key={i} className="text-xs border-b pb-1"><Badge variant="destructive" className="text-[10px]">{dn.denial_reason_code}</Badge> <span className="text-muted-foreground ml-1">{dn.denial_reason_text}</span> <span className="font-medium ml-1">{fmt(dn.denial_amount)}</span></div>)}
            </CardContent></Card>}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════
export default function RcmAnalyticsDashboard() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [overview, setOverview] = useState(null);
  const [aging, setAging] = useState(null);
  const [scorecards, setScorecards] = useState(null);
  const [denialData, setDenialData] = useState(null);
  const [underpayData, setUnderpayData] = useState(null);
  const [worklists, setWorklists] = useState([]);
  const [dataMode, setDataMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [drill, setDrill] = useState(null);
  const [payerId, setPayerId] = useState('');
  const [payers, setPayers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = payerId ? { payer_id: payerId } : {};
      const [accR, ovR, agR, scR, dnR, upR, wlR, dmR] = await Promise.all([
        api.get('/rcm-analytics/my-access'),
        api.get('/rcm-analytics/overview', { params }),
        api.get('/rcm-analytics/ar-aging', { params }),
        api.get('/rcm-analytics/payer-scorecards').catch(() => ({ data: { scorecards: [] } })),
        api.get('/rcm-analytics/denials-summary', { params }),
        api.get('/rcm-analytics/underpayments', { params }).catch(() => ({ data: { summary: {}, candidates: [] } })),
        api.get('/rcm-analytics/worklists').catch(() => ({ data: { worklists: [] } })),
        api.get('/rcm-analytics/admin/data-mode').catch(() => ({ data: null })),
      ]);
      setAccess(accR.data.access);
      setOverview(ovR.data);
      setAging(agR.data);
      setScorecards(scR.data.scorecards || []);
      setDenialData(dnR.data);
      setUnderpayData(upR.data);
      setWorklists(wlR.data.worklists || []);
      setDataMode(dmR.data);
      if (!payers.length) {
        const pl = ovR.data?.breakdown_by_payer?.filter(p => p.payer_id !== 'unknown') || [];
        setPayers(pl);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [payerId]);

  useEffect(() => { load(); }, [load]);

  const createWorklist = async (source, filters, title) => {
    try {
      await api.post('/rcm-analytics/worklists/from-analytics', { source, filters, title });
      const wlR = await api.get('/rcm-analytics/worklists');
      setWorklists(wlR.data.worklists || []);
      setTab('worklists');
    } catch (err) { console.error(err); }
  };

  if (loading && !overview) return (
    <div className="p-6 space-y-4" data-testid="rcm-analytics-loading">
      <div className="h-8 w-64 bg-muted animate-pulse rounded" />
      <div className="grid grid-cols-5 gap-3">{[1,2,3,4,5].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>
    </div>
  );

  const t = overview?.totals || {};
  const aging60 = aging?.buckets ? aging.buckets.filter(b => ['61-90','91-120','121+'].includes(b.label)).reduce((s,b) => s + b.amount, 0) : 0;
  const denRate = overview?.counts?.denials && overview?.counts?.claims ? ((overview.counts.denials / overview.counts.claims) * 100).toFixed(1) : '0';
  const upCount = underpayData?.summary?.possible_underpayment || 0;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1440px]" data-testid="rcm-analytics-dashboard">
      {/* Demo Banner */}
      {dataMode && dataMode.mode === 'demo' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center gap-2 text-sm" data-testid="demo-banner">
          <Info className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-amber-800">Demo Data Active — Analytics based on seeded data. Reset available in admin settings.</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="dashboard-title">Revenue Analytics</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Financial visibility and operational prioritization</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={payerId} onValueChange={v => setPayerId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px] h-7 text-xs" data-testid="filter-payer"><SelectValue placeholder="All Payers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payers</SelectItem>
              {payers.map(p => <SelectItem key={p.payer_id} value={p.payer_id}>{p.payer_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {payerId && <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setPayerId('')} data-testid="clear-filter"><X className="h-3 w-3" /></Button>}
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-strip">
        <Kpi title="Total A/R" value={fmtK(t.total_ar)} icon={Clock} color="text-amber-600" testId="kpi-total-ar" />
        <Kpi title="60+ Day A/R" value={fmtK(aging60)} icon={AlertTriangle} color="text-red-600" testId="kpi-ar-60" />
        <Kpi title="Denial Rate" value={`${denRate}%`} sub={`${overview?.counts?.denials || 0} denials`} icon={ShieldAlert} color={parseFloat(denRate) > 10 ? 'text-red-600' : ''} testId="kpi-denial-rate" />
        <Kpi title="Net Collections" value={fmtK(t.net_collections)} icon={TrendingUp} color="text-emerald-600" testId="kpi-net-coll" />
        <Kpi title="Underpayments" value={upCount} sub={upCount > 0 ? fmt(underpayData?.summary?.total_underpayment_dollars) : 'None detected'} icon={DollarSign} color={upCount > 0 ? 'text-amber-600' : ''} testId="kpi-underpay" />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} data-testid="analytics-tabs">
        <TabsList className="bg-muted/50 flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          {access && access.ar && <TabsTrigger value="aging" data-testid="tab-aging">A/R Aging</TabsTrigger>}
          <TabsTrigger value="payers" data-testid="tab-payers">Payers</TabsTrigger>
          {access && access.denials && <TabsTrigger value="denials" data-testid="tab-denials">Denials</TabsTrigger>}
          {access && access.payments && <TabsTrigger value="underpayments" data-testid="tab-underpay">Underpayments</TabsTrigger>}
          <TabsTrigger value="worklists" data-testid="tab-worklists" className="relative">
            Worklists {worklists.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1">{worklists.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ──── OVERVIEW ──── */}
        <TabsContent value="overview" className="space-y-4 mt-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">By Revenue Model</CardTitle></CardHeader><CardContent className="px-3 pb-3">
              {overview?.breakdown_by_revenue_model?.filter(r => r.revenue_model !== 'unknown').map((r,i) => {
                const max = Math.max(...(overview.breakdown_by_revenue_model || []).map(x => x.total));
                return (<div key={i} className="mb-2"><div className="flex justify-between text-xs"><span className="capitalize">{r.revenue_model.replace(/_/g,' ')}</span><span className="font-medium">{fmt(r.total)} <span className="text-muted-foreground">({r.count})</span></span></div><div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden"><div className="h-full bg-primary rounded-full" style={{width:`${max>0?(r.total/max)*100:0}%`}} /></div></div>);
              })}
            </CardContent></Card>
            <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Top Payers</CardTitle></CardHeader><CardContent className="px-3 pb-3">
              {overview?.breakdown_by_payer?.filter(p => p.payer_id !== 'unknown').slice(0,7).map((p,i) => {
                const max = Math.max(...(overview.breakdown_by_payer || []).map(x => x.total));
                return (<div key={i} className="flex items-center gap-2 mb-1.5"><span className="w-28 text-xs truncate">{p.payer_name}</span><div className="flex-1 h-4 bg-muted/30 rounded overflow-hidden"><div className="h-full bg-primary/20 rounded" style={{width:`${max>0?(p.total/max)*100:0}%`}} /></div><span className="w-16 text-right text-xs font-medium">{fmt(p.total)}</span><span className="w-6 text-right text-[10px] text-muted-foreground">{p.count}</span></div>);
              })}
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* ──── A/R AGING ──── */}
        <TabsContent value="aging" className="space-y-4 mt-3">
          <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Aging Distribution</CardTitle></CardHeader><CardContent className="px-3 pb-3">
            {aging?.buckets && (() => { const total = aging.buckets.reduce((s,b)=>s+b.amount,0); const colors = ['bg-emerald-500','bg-sky-500','bg-amber-500','bg-orange-500','bg-red-500']; return (<>
              <div className="flex h-5 w-full rounded overflow-hidden mb-3">{aging.buckets.map((b,i) => { const w = total>0?(b.amount/total)*100:0; return w > 0 ? <div key={b.label} className={`${colors[i]}`} style={{width:`${w}%`}} title={`${b.label}: ${fmt(b.amount)}`} /> : null; })}</div>
              <div className="grid grid-cols-5 gap-2">{aging.buckets.map((b,i) => (<div key={b.label} className="text-center"><div className={`h-1.5 w-1.5 rounded-full ${colors[i]} mx-auto mb-0.5`} /><p className="text-[10px] font-medium">{b.label}</p><p className="text-xs font-bold">{fmtK(b.amount)}</p><p className="text-[10px] text-muted-foreground">{b.count}</p></div>))}</div>
            </>); })()}
          </CardContent></Card>
          <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs flex justify-between">Aged Claims <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => createWorklist('ar_aging',{min_age_days:60},'A/R > 60 days')} data-testid="create-wl-aging"><ListChecks className="h-3 w-3 mr-1" />Create Worklist</Button></CardTitle></CardHeader><CardContent className="px-3 pb-3">
            <table className="w-full text-xs"><thead><tr className="border-b text-left"><th className="py-1 font-medium text-muted-foreground">Payer</th><th className="py-1 font-medium text-muted-foreground text-right">Balance</th><th className="py-1 font-medium text-muted-foreground text-right">Age</th><th className="py-1 font-medium text-muted-foreground">Status</th><th></th></tr></thead>
            <tbody>{aging?.detail?.slice(0,15).map((d,i) => (<tr key={i} className="border-b hover:bg-muted/30"><td className="py-1 truncate max-w-[140px]">{d.payer_name}</td><td className="py-1 text-right font-medium">{fmt(d.current_balance_amount)}</td><td className="py-1 text-right"><Badge variant={d.age_days>90?'destructive':d.age_days>60?'default':'secondary'} className="text-[10px]">{d.age_days}d</Badge></td><td className="py-1"><Badge variant="outline" className="text-[10px]">{d.status}</Badge></td><td className="py-1"><Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={()=>setDrill(d.claim_id)}><ChevronRight className="h-3 w-3" /></Button></td></tr>))}</tbody></table>
          </CardContent></Card>
        </TabsContent>

        {/* ──── PAYERS (Scorecards) ──── */}
        <TabsContent value="payers" className="space-y-4 mt-3">
          <div className="space-y-2" data-testid="payer-scorecards">
            {scorecards?.map((sc) => (
              <Card key={sc.payer_id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-3">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                      <span className={`text-lg font-bold ${sc.overall_score >= 80 ? 'text-emerald-600' : sc.overall_score >= 60 ? 'text-sky-600' : sc.overall_score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{Math.round(sc.overall_score)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{sc.payer_name}</span>
                        <TierBadge tier={sc.performance_tier} />
                        <Badge variant="outline" className="text-[10px]">{sc.confidence_level}</Badge>
                        <TrendIcon dir={sc.trend_direction} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{sc.top_issue}</p>
                    </div>
                    <div className="hidden md:grid grid-cols-4 gap-4 text-center">
                      <div><p className="text-[10px] text-muted-foreground">Clean</p><p className="text-sm font-medium">{(sc.metrics.clean_claim_rate * 100).toFixed(0)}%</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Deny</p><p className="text-sm font-medium">{(sc.metrics.denial_rate * 100).toFixed(0)}%</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Days</p><p className="text-sm font-medium">{sc.metrics.avg_days_to_payment.toFixed(0)}d</p></div>
                      <div><p className="text-[10px] text-muted-foreground">A/R</p><p className="text-sm font-medium">{fmt(sc.dollars.balance)}</p></div>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => createWorklist('slow_payers',{min_days:30,payer_id:sc.payer_id},`${sc.payer_name} worklist`)} data-testid={`wl-payer-${sc.payer_id}`}>
                      <ListChecks className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ──── DENIALS ──── */}
        <TabsContent value="denials" className="space-y-4 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Kpi title="Total Denials" value={denialData?.denials?.count || 0} sub={`${denialData?.denials?.open_count || 0} open`} icon={AlertTriangle} color="text-red-600" testId="kpi-den-count" />
            <Kpi title="Denial Amount" value={fmt(denialData?.denials?.total_denial_amount)} icon={DollarSign} color="text-red-600" testId="kpi-den-amt" />
            <Kpi title="Rejections" value={denialData?.rejections?.count || 0} sub={`${denialData?.rejections?.open_count || 0} open`} icon={FileText} color="text-amber-600" testId="kpi-rej" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Top Reasons</CardTitle></CardHeader><CardContent className="px-3 pb-3">
              {denialData?.denials?.top_reasons?.map((r,i) => (<div key={i} className="flex justify-between py-1 border-b last:border-0 text-xs"><div><span className="font-medium">{r.code}</span><p className="text-muted-foreground truncate max-w-[200px]">{r.text}</p></div><div className="text-right"><Badge variant="secondary" className="text-[10px]">{r.count}</Badge><p className="text-muted-foreground mt-0.5">{fmt(r.total_amount)}</p></div></div>))}
            </CardContent></Card>
            <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs flex justify-between">Categories <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => createWorklist('denial_clusters',{},'All open denials')} data-testid="create-wl-denials"><ListChecks className="h-3 w-3 mr-1" />Worklist</Button></CardTitle></CardHeader><CardContent className="px-3 pb-3">
              {denialData?.denials?.top_categories?.map((c,i) => (<div key={i} className="flex justify-between py-1 border-b last:border-0 text-xs"><span className="capitalize">{c.category}</span><div className="flex gap-2"><Badge variant="outline" className="text-[10px]">{c.count}</Badge><span className="font-medium">{fmt(c.total_amount)}</span></div></div>))}
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* ──── UNDERPAYMENTS ──── */}
        <TabsContent value="underpayments" className="space-y-4 mt-3">
          {underpayData && (<>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi title="Underpayments" value={underpayData.summary?.possible_underpayment || 0} sub={fmt(underpayData.summary?.total_underpayment_dollars)} icon={DollarSign} color="text-red-600" testId="kpi-up-count" />
              <Kpi title="Analyzed" value={underpayData.summary?.total_analyzed || 0} icon={BarChart3} testId="kpi-up-analyzed" />
              <Kpi title="Expected" value={underpayData.summary?.expected || 0} sub="Within threshold" icon={TrendingUp} color="text-emerald-600" testId="kpi-up-ok" />
              <Kpi title="Inconclusive" value={underpayData.summary?.inconclusive || 0} sub="Needs review" icon={AlertTriangle} testId="kpi-up-inconc" />
            </div>
            <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs flex justify-between">Candidates <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => createWorklist('underpayments',{priority:'high'},'High-priority underpayments')} data-testid="create-wl-underpay"><ListChecks className="h-3 w-3 mr-1" />Create Worklist</Button></CardTitle></CardHeader><CardContent className="px-3 pb-3">
              <table className="w-full text-xs"><thead><tr className="border-b text-left"><th className="py-1 font-medium text-muted-foreground">Payer</th><th className="py-1 font-medium text-muted-foreground">CPT</th><th className="py-1 font-medium text-muted-foreground text-right">Expected</th><th className="py-1 font-medium text-muted-foreground text-right">Paid</th><th className="py-1 font-medium text-muted-foreground text-right">Variance</th><th className="py-1 font-medium text-muted-foreground">Priority</th><th></th></tr></thead>
              <tbody>{underpayData.candidates?.slice(0,15).map((c,i) => { const b = c.breakdown; return (
                <tr key={i} className="border-b hover:bg-muted/30"><td className="py-1 truncate max-w-[130px]">{c.payer_name}</td><td className="py-1">{c.cpt_code}</td><td className="py-1 text-right">{fmt(b.expected_amount)}</td><td className="py-1 text-right">{fmt(b.actual_paid)}</td><td className="py-1 text-right font-medium text-red-600">{fmt(b.variance)}</td><td className="py-1"><Badge variant={c.review_priority==='high'?'destructive':c.review_priority==='medium'?'default':'secondary'} className="text-[10px]">{c.review_priority}</Badge></td><td className="py-1"><Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={()=>setDrill(c.claim_id)}><ChevronRight className="h-3 w-3" /></Button></td></tr>
              ); })}</tbody></table>
            </CardContent></Card>
          </>)}
        </TabsContent>

        {/* ──── WORKLISTS ──── */}
        <TabsContent value="worklists" className="space-y-4 mt-3">
          {worklists.length === 0 ? (
            <Card><CardContent className="p-8 text-center"><ListChecks className="h-8 w-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No worklists yet</p><p className="text-xs text-muted-foreground mt-1">Generate worklists from A/R Aging, Denials, or Underpayments tabs</p></CardContent></Card>
          ) : (
            <div className="space-y-2" data-testid="worklist-list">
              {worklists.map((wl) => (
                <WorklistRow key={wl.id} wl={wl} onDrill={setDrill} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ClaimPanel id={drill} onClose={() => setDrill(null)} />
    </div>
  );
}

// ─── WORKLIST ROW (expandable) ───
function WorklistRow({ wl, onDrill }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);

  const toggle = async () => {
    if (!open && !items) {
      try {
        const r = await api.get(`/rcm-analytics/worklists/${wl.id}`);
        setItems(r.data.items || []);
      } catch { setItems([]); }
    }
    setOpen(!open);
  };

  return (
    <Card data-testid={`worklist-${wl.id}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-3 cursor-pointer" onClick={toggle}>
          <div className="w-8 h-8 rounded bg-muted flex items-center justify-center shrink-0">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{wl.title}</p>
            <p className="text-[10px] text-muted-foreground">{wl.source} — {wl.item_count} items — {wl.created_at?.slice(0,10)}</p>
          </div>
          <Badge variant="outline" className="text-[10px]">{wl.item_count}</Badge>
          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
        </div>
        {open && items && (
          <div className="mt-3 border-t pt-2">
            <table className="w-full text-xs"><thead><tr className="text-left"><th className="py-1 font-medium text-muted-foreground">#</th><th className="py-1 font-medium text-muted-foreground">Payer</th><th className="py-1 font-medium text-muted-foreground text-right">Amount</th><th className="py-1 font-medium text-muted-foreground text-right">Priority</th><th className="py-1 font-medium text-muted-foreground">Reason</th><th></th></tr></thead>
            <tbody>{items.slice(0,20).map((it,i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-muted/30"><td className="py-1 text-muted-foreground">{it.item_index}</td><td className="py-1 truncate max-w-[120px]">{it.payer_name}</td><td className="py-1 text-right font-medium">{fmt(it.balance)}</td><td className="py-1 text-right"><Badge variant={it.priority_score > 60 ? 'destructive' : it.priority_score > 30 ? 'default' : 'secondary'} className="text-[10px]">{it.priority_score?.toFixed(0)}</Badge></td><td className="py-1 truncate max-w-[250px] text-muted-foreground">{it.reason}</td><td className="py-1">{it.claim_id && <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={(e) => { e.stopPropagation(); onDrill(it.claim_id); }}><ChevronRight className="h-3 w-3" /></Button>}</td></tr>
            ))}</tbody></table>
            {items.length > 20 && <p className="text-[10px] text-muted-foreground text-center mt-2">Showing 20 of {items.length} items</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
